import { useCallback, useState, useEffect, useMemo } from 'react';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { FullChatLayout } from './components/layouts/FullChatLayout';
import { SidebarLayout } from './components/layouts/SidebarLayout';
import { FloatingLayout } from './components/layouts/FloatingLayout';
import { useDevXGPT } from './hooks/useDevXGPT';
import { useAssistants } from './hooks/useAssistants';
import { getResponsiveFloatingWindowSize } from './util/cssVariables';
import './styles/main.css';

export interface DevXGPTChatProps {
  /** Display mode: 'sidebar', 'full', or 'floating' */
  mode?: 'sidebar' | 'full' | 'floating';
  /** Authentication token for API calls */
  authToken?: string;
  /** Specific assistant ID to use. If not provided, uses build-time default from VITE_DEVXGPT_DEFAULT_ASSISTANT_ID */
  assistantId?: string;
  /** Callback fired when a message is sent */
  onMessageSent?: (message: string) => void;
  /** Callback fired when user selects an assistant. Useful for analytics, state sync, or deep-linking. */
  onAssistantSelected?: (assistantId: string) => void;
  /** Callback fired when user selects a conversation. Useful for analytics, URL sync, or state management. */
  onConversationSelected?: (conversationId: string) => void;
  /** Callback fired when user starts a new conversation. Useful for analytics, notifications, or state reset. */
  onNewConversation?: () => void;
  /** Auto-fetch conversations on mount */
  autoFetch?: boolean;
  /** Use mock API instead of real API (for development) */
  useMock?: boolean;
  /** Optional callback fired when user clicks expand button. Built-in mode switching always occurs and this callback fires after. */
  onExpand?: () => void;
  /** Show assistants view in sidebar */
  showAssistantsView?: boolean;
  /** Show conversation history view in sidebar */
  showHistoryView?: boolean;
  /** Show the built-in Usage Guidelines link in the footer. Defaults to true. */
  showUsageGuidelines?: boolean;
  /** Optional text shown in the footer alongside the usage guidelines link (e.g. app name or short disclaimer). */
  footerText?: string;
  /** Behaviour of sidebar (sidebar mode only) */
  sidebarMode?: 'push' | 'overlay';
  /** Optional callback when sidebar panel opens or closes. Body class 'devxgpt-sidebar-open' is automatically managed. */
  onSidebarOpenChange?: (isOpen: boolean) => void;
  /** Initial open state */
  initiallyOpen?: boolean;
  /** Enable drag for floating window */
  floatingEnableDrag?: boolean;
  /** Enable resize for floating window */
  floatingEnableResize?: boolean;
  /** Callback when floating chat opens or closes */
  onFloatingOpenChange?: (isOpen: boolean) => void;
  /** Navigation layout for floating mode: 'sidebar' (vertical nav) or 'horizontal' (top nav with logo) */
  floatingNavigationLayout?: 'sidebar' | 'horizontal';
  /** Callback when user pins or unpins the floating chat */
  onModeChange?: (mode: 'sidebar' | 'full' | 'floating') => void;
}

/**
 * DevXGPTChat Component (Orchestrator)
 *
 * Main component that orchestrates the chat interface.
 * Supports three modes: sidebar, full, and floating.
 */
export function DevXGPTChat({
  mode: initialMode = 'floating',
  authToken,
  assistantId,
  onMessageSent,
  onAssistantSelected,
  onConversationSelected,
  onNewConversation,
  autoFetch = true,
  useMock = false,
  onExpand,
  showAssistantsView = true,
  showHistoryView = true,
  showUsageGuidelines,
  footerText,
  sidebarMode = 'overlay',
  onSidebarOpenChange,
  initiallyOpen = false,
  floatingEnableDrag = true,
  floatingEnableResize = true,
  onFloatingOpenChange,
  floatingNavigationLayout = 'horizontal',
  onModeChange,
}: DevXGPTChatProps): JSX.Element {
  const [currentMode, setCurrentMode] = useState<'sidebar' | 'full' | 'floating'>(initialMode);
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [activeSidebarView, setActiveSidebarView] = useState<'assistants' | 'history' | 'chat'>('chat');
  
  // Initialize floating size from CSS variables (responsive to viewport and DPI scaling)
  const initialFloatingSize = useMemo(() => getResponsiveFloatingWindowSize(), []);
  const [floatingSize, setFloatingSize] = useState(initialFloatingSize);
  
  // Position will be managed by FloatingLayout (bottom-right by default, or user-dragged position)
  const [floatingPosition, setFloatingPosition] = useState<{ x: number; y: number } | undefined>(undefined);
  const [sidebarWidth, setSidebarWidth] = useState(400);

  // Listen for viewport/DPI changes and update floating window size and position accordingly
  useEffect(() => {
    const handleResize = () => {
      const newSize = getResponsiveFloatingWindowSize();
      setFloatingSize(newSize);
      
      // If user hasn't manually positioned the window, keep it in bottom-right
      // (indicated by floatingPosition still being undefined, which means we use the default)
      // If user has positioned it, don't auto-adjust on resize
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // console.log(
  //   `%c[DevXGPTChat] currentMode=${currentMode}, isOpen=${isOpen}, view=${activeSidebarView}`,
  //   'color: blue; font-weight: bold;'
  // );

  useEffect(() => {
    if (currentMode !== 'sidebar') return;

    if (sidebarMode === 'overlay') {
      document.body.classList.add('devxgpt-sidebar-mode-overlay');
      document.body.classList.remove('devxgpt-sidebar-mode-push');
    } else {
      document.body.classList.add('devxgpt-sidebar-mode-push');
      document.body.classList.remove('devxgpt-sidebar-mode-overlay');
    }

    document.body.classList.toggle('devxgpt-sidebar-open', isOpen);
    document.body.classList.toggle('devxgpt-sidebar-closed', !isOpen);

    return () => {
      document.body.classList.remove('devxgpt-sidebar-open');
      if (sidebarMode === 'push') {
        document.body.classList.add('devxgpt-sidebar-mode-push-closed');
        document.body.classList.remove('devxgpt-sidebar-mode-push-open');
      }
    };
  }, [isOpen, currentMode, sidebarMode]);

  const assistantsOptions = useMemo(
    () => ({
      authToken,
      useMock,
      autoFetch,
      defaultAssistantId: assistantId,
    }),
    [authToken, useMock, autoFetch, assistantId]
  );

  const {
    assistants,
    selectedAssistant,
    selectAssistantById,
    isLoading: isLoadingAssistants,
    fetchAssistants: refetchAssistants,
  } = useAssistants(assistantsOptions);

  const {
    conversations,
    messages,
    currentConversation,
    isSending,
    isLoading,
    streamingMessage,
    error,
    sendMessage,
    startNewConversation,
    selectConversation,
    clearError,
    fetchConversations: refetchConversations
  } = useDevXGPT({
    authToken,
    assistantId: selectedAssistant?.assistant_id || assistantId,
    autoFetch,
    useMock,
  });

  // Handle sending a message
  const handleSendMessage = useCallback(
    async (message: string) => {
      // Send message through API
      await sendMessage(message, currentConversation?.conversation_id);

      // Call the callback if provided
      onMessageSent?.(message);
    },
    [onMessageSent, sendMessage, currentConversation]
  );

  const handleNewConversation = useCallback(() => {
    startNewConversation();
    onNewConversation?.();
  }, [startNewConversation, onNewConversation]);

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      const conversation = conversations.find((c) => c.conversation_id === conversationId);
      if (conversation) {
        selectConversation(conversation);
        setActiveSidebarView('chat');
        onConversationSelected?.(conversationId);
      }
    },
    [selectConversation, conversations, onConversationSelected]
  );

  const handleSelectAssistant = useCallback(
    (assistantId: string) => {
      // Use selectAssistantById from hook - it handles all fallback logic
      selectAssistantById(assistantId);

      startNewConversation();
      onAssistantSelected?.(assistantId);
      onNewConversation?.();
    },
    [selectAssistantById, startNewConversation, onAssistantSelected, onNewConversation]
  );

  // Handle refresh action
  const handleRefresh = useCallback(() => {
    if (activeSidebarView === 'assistants') {
      refetchAssistants();
    } else if (activeSidebarView === 'history') {
      refetchConversations();
    }
  }, [activeSidebarView, refetchAssistants, refetchConversations]);

  const handleSidebarOpenChange = useCallback(
    (isOpenParam: boolean) => {
      setIsOpen(isOpenParam);
      onSidebarOpenChange?.(isOpenParam);
    },
    [onSidebarOpenChange]
  );

  const handleModeChange = useCallback(
    (newMode: 'sidebar' | 'full' | 'floating') => {
      setCurrentMode(newMode);
      onModeChange?.(newMode);
    },
    [onModeChange]
  );

  const handlePinToggle = useCallback(() => {
    handleModeChange(currentMode === 'floating' ? 'sidebar' : 'floating');
  }, [currentMode, handleModeChange]);

  // Handle expand action - toggle between sidebar and full mode
  const handleExpand = useCallback(() => {
    handleModeChange(currentMode === 'sidebar' ? 'full' : 'sidebar');
    onExpand?.();
  }, [onExpand, currentMode, handleModeChange]);

  const effectiveAssistantId =
    currentConversation?.assistant_id ||
    selectedAssistant?.assistant_id ||
    assistantId;

  if (currentMode === 'floating') {
    return (
      <ErrorBoundary>
        <FloatingLayout
          messages={messages}
          streamingMessage={streamingMessage}
          isSending={isSending}
          isLoading={isLoading}
          error={error}
          onSendMessage={handleSendMessage}
          onClearError={clearError}
          assistants={assistants}
          currentAssistantId={effectiveAssistantId}
          onSelectAssistant={handleSelectAssistant}
          isLoadingAssistants={isLoadingAssistants}
          conversations={conversations}
          currentConversationId={currentConversation?.conversation_id}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewConversation}
          isLoadingConversations={isLoading}
          onExpand={handleExpand}
          onRefresh={handleRefresh}
          showExpand={true}
          showRefresh={true}
          showAssistantsView={showAssistantsView}
          showHistoryView={showHistoryView}
          showUsageGuidelines={showUsageGuidelines}
          footerText={footerText}
          enableDrag={floatingEnableDrag}
          enableResize={floatingEnableResize}
          onOpenChange={onFloatingOpenChange}
          activeSidebarView={activeSidebarView}
          onSidebarViewChange={setActiveSidebarView}
          isOpen={isOpen}
          onIsOpenChange={setIsOpen}
          navigationLayout={floatingNavigationLayout}
          onPin={handlePinToggle}
          defaultSize={floatingSize}
          onSizeChange={setFloatingSize}
          defaultPosition={floatingPosition}
          onPositionChange={setFloatingPosition}
        />
      </ErrorBoundary>
    );
  }

  if (currentMode === 'sidebar') {
    return (
      <ErrorBoundary>
        <SidebarLayout
          messages={messages}
          streamingMessage={streamingMessage}
          isSending={isSending}
          isLoading={isLoading}
          error={error}
          onSendMessage={handleSendMessage}
          onClearError={clearError}
          assistants={assistants}
          currentAssistantId={effectiveAssistantId}
          onSelectAssistant={handleSelectAssistant}
          isLoadingAssistants={isLoadingAssistants}
          conversations={conversations}
          currentConversationId={currentConversation?.conversation_id}
          onSelectConversation={handleSelectConversation}
          onNewChat={handleNewConversation}
          isLoadingConversations={isLoading}
          onExpand={handleExpand}
          onRefresh={handleRefresh}
          showExpand={true}
          showRefresh={true}
          showAssistantsView={showAssistantsView}
          showHistoryView={showHistoryView}
          showUsageGuidelines={showUsageGuidelines}
          footerText={footerText}
          onOpenChange={handleSidebarOpenChange}
          activeSidebarView={activeSidebarView}
          onSidebarViewChange={setActiveSidebarView}
          sidebarMode={sidebarMode}
          onUnpin={handlePinToggle}
          isOpen={isOpen}
          sidebarWidth={sidebarWidth}
          onSidebarWidthChange={setSidebarWidth}
        />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <FullChatLayout
        conversations={conversations}
        currentConversation={currentConversation || null}
        messages={messages}
        streamingMessage={streamingMessage}
        isSending={isSending}
        isLoading={isLoading}
        error={error}
        onSendMessage={handleSendMessage}
        onSelectConversation={selectConversation}
        onNewConversation={handleNewConversation}
        onClearError={clearError}
        showUsageGuidelines={showUsageGuidelines}
        footerText={footerText}
      />
    </ErrorBoundary>
  );
};
